import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

export default function ScatterPlot({ 
  data = [], 
  xKey = 'x', 
  yKey = 'y', 
  xLabel, 
  yLabel, 
  title,
  correlation,
  showTrendLine = true,
  colorKey = null,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444']
}) {
  // Validate data
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p>No data available for scatter plot</p>
      </div>
    );
  }
  // Calculate trend line if correlation is provided
  const trendLine = showTrendLine && correlation ? calculateTrendLine(data, xKey, yKey) : null;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const point = payload[0].payload;
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
          <p className="text-slate-300 text-sm">
            <span className="font-semibold">{xLabel || xKey}:</span> {typeof point[xKey] === 'number' ? point[xKey].toFixed(2) : point[xKey]}
          </p>
          <p className="text-slate-300 text-sm">
            <span className="font-semibold">{yLabel || yKey}:</span> {typeof point[yKey] === 'number' ? point[yKey].toFixed(2) : point[yKey]}
          </p>
          {point.name && (
            <p className="text-slate-400 text-xs mt-1">{point.name}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full">
      {title && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
          {correlation && (
            <div className="flex items-center gap-4 mt-2">
              <span className="text-sm text-slate-400">
                Correlation: <span className={`font-semibold ${getCorrelationColor(correlation.coefficient)}`}>
                  {correlation.coefficient.toFixed(3)}
                </span>
              </span>
              <span className="text-sm text-slate-400">
                Strength: <span className="font-semibold text-slate-300">{correlation.strength}</span>
              </span>
              <span className="text-sm text-slate-400">
                n = {correlation.sampleSize}
              </span>
            </div>
          )}
        </div>
      )}
      
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis 
            type="number" 
            dataKey={xKey} 
            name={xLabel || xKey}
            label={{ value: xLabel || xKey, position: 'insideBottom', offset: -10, fill: '#94a3b8' }}
            stroke="#64748b"
            tick={{ fill: '#94a3b8' }}
          />
          <YAxis 
            type="number" 
            dataKey={yKey} 
            name={yLabel || yKey}
            label={{ value: yLabel || yKey, angle: -90, position: 'insideLeft', fill: '#94a3b8' }}
            stroke="#64748b"
            tick={{ fill: '#94a3b8' }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
          />
          
          {/* Trend line */}
          {trendLine && (
            <ReferenceLine 
              segment={[
                { x: trendLine.x1, y: trendLine.y1 },
                { x: trendLine.x2, y: trendLine.y2 }
              ]}
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ value: 'Trend', fill: '#f59e0b', fontSize: 12 }}
            />
          )}
          
          <Scatter 
            name={title || 'Data Points'} 
            data={data} 
            fill="#3b82f6"
            fillOpacity={0.6}
            stroke="#3b82f6"
            strokeWidth={1}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

// Helper function to calculate trend line
function calculateTrendLine(data, xKey, yKey) {
  if (!data || data.length < 2) return null;

  const n = data.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

  data.forEach(point => {
    const x = point[xKey];
    const y = point[yKey];
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const xMin = Math.min(...data.map(d => d[xKey]));
  const xMax = Math.max(...data.map(d => d[xKey]));

  return {
    x1: xMin,
    y1: slope * xMin + intercept,
    x2: xMax,
    y2: slope * xMax + intercept
  };
}

// Helper function to get correlation color
function getCorrelationColor(coefficient) {
  const abs = Math.abs(coefficient);
  if (abs > 0.7) return 'text-green-400';
  if (abs > 0.4) return 'text-yellow-400';
  if (abs > 0.2) return 'text-orange-400';
  return 'text-slate-400';
}
